import React, { useState, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { useNavigate, useParams } from 'react-router-dom';
import './updatetable.scss';
import Navbar from '../navbar/Navbar';
import Sidebar from '../sidebar/Sidebar';


const Updatetable = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    tableno: '',
    status: 'available',
    shape: 'circle',
    x: 0,
    y: 0,
    width: 150,
    height: 150,
  });

  const [existingTables, setExistingTables] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const storedToken = localStorage.getItem('accessToken');
        if (!storedToken) throw new Error('Access token not found');

        // 1. Fetch current table
        const tableRes = await fetch(`${apiUrl}/tables/find/${id}`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        if (!tableRes.ok) throw new Error('Failed to load table');
        const tableData = await tableRes.json();

        setFormData({
          tableno: tableData.tableno || '',
          status: tableData.status || 'available',
          shape: tableData.shape || 'circle',
          x: tableData.x ?? 0,
          y: tableData.y ?? 0,
          width: tableData.width ?? 150,
          height: tableData.height ?? 150,
        });

        // 2. Fetch all tables for the same merchant (backend already filters)
        const tablesRes = await fetch(`${apiUrl}/tables`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        if (tablesRes.ok) {
          const tablesData = await tablesRes.json();
          setExistingTables(tablesData);
        }
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message || 'Failed to load table data');
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, id]);

  // Auto-clear messages
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ['x', 'y', 'width', 'height'].includes(name) ? Number(value) : value,
    }));
  };

  const validateForm = () => {
    if (!formData.tableno.trim()) {
      setErrorMessage('Table number is required');
      return false;
    }
    if (formData.width <= 0 || formData.height <= 0) {
      setErrorMessage('Width and height must be positive');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    if (!validateForm()) return;

    setLoading(true);
    try {
      const storedToken = localStorage.getItem('accessToken');
      if (!storedToken) throw new Error('No access token found');

      const response = await fetch(`${apiUrl}/tables/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update table');

      setSuccessMessage('Table updated successfully!');
      setTimeout(() => navigate('/tables/tablelist'), 1000);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStop = (e, d) => {
    setFormData((prev) => ({ ...prev, x: d.x, y: d.y }));
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    setFormData((prev) => ({
      ...prev,
      width: ref.offsetWidth,      // ✅ more reliable than parseInt
      height: ref.offsetHeight,
      x: position.x,
      y: position.y,
    }));
  };

  if (pageLoading) {
    return (
      <div className="addtable">
        <Sidebar />
        <div className="newContainer">
          <Navbar />
          <div className="top"><h1>Loading Update Table...</h1></div>
        </div>
      </div>
    );
  }

  return (
    <div className="addtable">
      <Sidebar />
      <div className="newContainer">
        <Navbar />
        <div className="top"><h1>Update Table</h1></div>
        <div className="bottom">
          <div className="right">
            {successMessage && <p className="success-message">{successMessage}</p>}
            {errorMessage && <p className="error-message">{errorMessage}</p>}

            <form onSubmit={handleSubmit}>
              <div className="formInput">
                <label>Table No *</label>
                <input type="text" name="tableno" value={formData.tableno} onChange={handleChange} required />
              </div>
              <div className="formInput">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleChange}>
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="reserved">Reserved</option>
                </select>
              </div>
              <div className="formInput">
                <label>Shape</label>
                <select name="shape" value={formData.shape} onChange={handleChange}>
                  <option value="circle">Circle</option>
                  <option value="rectangle">Rectangle</option>
                </select>
              </div>
              <div className="formInput">
                <label>Width *</label>
                <input type="number" name="width" value={formData.width} onChange={handleChange} min="1" required />
              </div>
              <div className="formInput">
                <label>Height *</label>
                <input type="number" name="height" value={formData.height} onChange={handleChange} min="1" required />
              </div>
              <button type="submit" disabled={loading}>{loading ? 'Updating...' : 'Update Table'}</button>
            </form>

            {/* Floor Plan */}
            <div
              className="floor-plan"
              style={{
                position: 'relative',
                width: '100%',
                height: '700px',
                border: '2px solid #ccc',
                marginTop: '20px',
                background: '#f0f0f0',
                backgroundImage: `linear-gradient(to right, #ddd 1px, transparent 1px), linear-gradient(to bottom, #ddd 1px, transparent 1px)`,
                backgroundSize: '20px 20px',
                overflow: 'auto',
              }}
            >
              {/* Other tables (static) */}
              {existingTables
                .filter((table) => table._id !== id)
                .map((table) => (
                  <div
                    key={table._id}
                    style={{
                      position: 'absolute',
                      left: table.x ?? 0,
                      top: table.y ?? 0,
                      width: table.width ?? 150,
                      height: table.height ?? 150,
                      borderRadius: table.shape === 'circle' ? '50%' : '4px',
                      backgroundColor: '#51910c',
                      opacity: 0.6,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      border: '1px solid rgba(0,0,0,0.2)',
                      fontWeight: '600',
                    }}
                  >
                    {table.tableno}
                  </div>
                ))}

              {/* Editable table */}
              <Rnd
                size={{ width: formData.width, height: formData.height }}
                position={{ x: formData.x, y: formData.y }}
                onDragStop={handleDragStop}
                onResizeStop={handleResizeStop}
                bounds="parent"
                enableResizing={{
                  top: true, right: true, bottom: true, left: true,
                  topRight: true, bottomRight: true, bottomLeft: true, topLeft: true,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: formData.shape === 'circle' ? '50%' : '4px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'move',
                    userSelect: 'none',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    fontWeight: '600',
                  }}
                >
                  {formData.tableno || 'Update Table'}
                </div>
              </Rnd>
            </div>
            <p style={{ marginTop: '8px', fontSize: '0.9rem', color: '#555' }}>
              Drag to move · Drag corners/edges to resize
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Updatetable;